<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, useId, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import ActionConfirmDialog from '@/components/ActionConfirmDialog.vue'
import { useAuthStore } from '@/stores/auth'

const auth = useAuthStore()
const route = useRoute()
const router = useRouter()
const root = ref<HTMLElement | null>(null)
const trigger = ref<HTMLButtonElement | null>(null)
const card = ref<HTMLElement | null>(null)
const open = ref(false)
const pinned = ref(false)
const logoutOpen = ref(false)
const loggingOut = ref(false)
const logoutAccount = ref('')
let logoutRevision = ''
let closeTimer: ReturnType<typeof setTimeout> | undefined
const cardId = useId()
const titleId = useId()
const adminAppUrl = import.meta.env.VITE_ADMIN_APP_URL ?? '/admin/'

function stopTimer() { clearTimeout(closeTimer) }
function close() { stopTimer(); open.value = false; pinned.value = false }
function hover(event: PointerEvent) {
  stopTimer()
  if (event.pointerType === 'mouse' && !logoutOpen.value) open.value = true
}
function leave() {
  stopTimer()
  closeTimer = setTimeout(() => {
    if (!pinned.value && !root.value?.contains(document.activeElement)) close()
  }, 160)
}
function toggle() {
  stopTimer()
  if (pinned.value) close()
  else { pinned.value = true; open.value = true }
}
async function openFromKeyboard() {
  pinned.value = true
  open.value = true
  await nextTick()
  card.value?.querySelector<HTMLAnchorElement>('a')?.focus()
}
function focusOut(event: FocusEvent) {
  if (!root.value?.contains(event.relatedTarget as Node | null)) close()
}
function outside(event: PointerEvent) {
  if (!root.value?.contains(event.target as Node)) close()
}
function escape(event: KeyboardEvent) {
  if (event.key === 'Escape' && open.value) {
    event.preventDefault()
    close()
    trigger.value?.focus()
  }
}
function requestLogout() {
  logoutAccount.value = auth.user?.username ?? ''
  logoutRevision = auth.sessionRevision
  close()
  trigger.value?.focus()
  logoutOpen.value = true
}
async function confirmLogout() {
  if (loggingOut.value || !auth.isCurrentSession(logoutRevision)) { logoutOpen.value = false; return }
  loggingOut.value = true
  const completion = auth.logout()
  await router.replace({ name: 'login' })
  await completion
  loggingOut.value = false
  logoutOpen.value = false
}
watch(() => route.fullPath, close)
watch(() => auth.sessionRevision, () => { close(); logoutOpen.value = false })
onMounted(() => {
  document.addEventListener('pointerdown', outside)
  document.addEventListener('keydown', escape)
})
onBeforeUnmount(() => {
  stopTimer()
  document.removeEventListener('pointerdown', outside)
  document.removeEventListener('keydown', escape)
})
</script>

<template>
  <div ref="root" class="account-menu" @pointerenter="hover" @pointerleave="leave" @focusout="focusOut">
    <button ref="trigger" class="account-menu__trigger" type="button" aria-label="個人資料" :aria-expanded="open" :aria-controls="cardId" @click="toggle" @keydown.down.prevent="openFromKeyboard">
      <span class="account-menu__avatar" aria-hidden="true">{{ Array.from(auth.displayName)[0]?.toUpperCase() }}</span>
      <span class="account-menu__identity"><strong>{{ auth.displayName }}</strong><small>個人資料</small></span>
      <svg aria-hidden="true" viewBox="0 0 16 16" width="16" height="16" :class="{ 'account-menu__chevron--open': open }"><path d="m4 6 4 4 4-4" /></svg>
    </button>
    <div v-if="open" class="account-menu__panel">
      <section :id="cardId" ref="card" class="account-menu__card" :aria-labelledby="titleId">
        <header><h2 :id="titleId">個人資料</h2><span>{{ auth.displayName }}</span></header>
        <dl>
          <div><dt>使用者名稱</dt><dd>{{ auth.user?.username }}</dd></div>
          <div><dt>電子信箱</dt><dd>{{ auth.user?.email }}</dd></div>
        </dl>
        <nav class="account-menu__links" aria-label="帳戶選單">
          <RouterLink to="/profile" @click="close"><span>資料管理</span><span aria-hidden="true">→</span></RouterLink>
          <a v-if="auth.isAdmin" :href="adminAppUrl" class="account-menu__admin"><span>管理後臺</span><span aria-hidden="true">↗</span></a>
          <button type="button" class="account-menu__logout" @click="requestLogout">登出帳號</button>
        </nav>
      </section>
    </div>
  </div>
  <ActionConfirmDialog :open="logoutOpen" title="確認登出？" eyebrow="帳戶操作" :details="[{ label: '使用者名稱', value: logoutAccount, wide: true }]" confirm-label="確認登出" cancel-label="繼續使用" :pending="loggingOut" pending-label="正在登出…" @confirm="confirmLogout" @cancel="logoutOpen = false" />
</template>

<style scoped>
.account-menu { position: relative; }
.account-menu__trigger { display: flex; min-height: 48px; align-items: center; gap: 10px; max-width: 220px; padding: 5px 10px; border: 1px solid var(--line); border-radius: 12px; background: var(--surface); color: var(--text); cursor: pointer; text-align: left; }
.account-menu__trigger:hover, .account-menu__trigger[aria-expanded="true"] { border-color: var(--line-strong); background: var(--surface-2); }
.account-menu__trigger:focus-visible, .account-menu__links a:focus-visible, .account-menu__links button:focus-visible { outline: 2px solid var(--focus-ring); outline-offset: 3px; }
.account-menu__avatar { display: grid; flex: 0 0 34px; width: 34px; height: 34px; place-items: center; border-radius: 50%; background: var(--accent-soft); color: var(--accent); font-size: .85rem; font-weight: 800; }
.account-menu__identity { display: flex; min-width: 0; flex-direction: column; gap: 2px; }
.account-menu__identity strong { max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: .8rem; }
.account-menu__identity small { color: var(--muted); font-size: .68rem; }
.account-menu__trigger svg { flex-shrink: 0; fill: none; stroke: var(--muted); stroke-width: 1.5; transition: transform .15s; }
.account-menu__chevron--open { transform: rotate(180deg); }
.account-menu__panel { position: absolute; top: 100%; right: 0; z-index: 50; width: min(320px, calc(100vw - 28px)); padding-top: 10px; }
.account-menu__card { overflow: auto; max-height: calc(100dvh - 160px); padding: 20px; border: 1px solid var(--line-strong); border-radius: 16px; background: var(--surface); box-shadow: var(--shadow); }
.account-menu__card header { display: grid; gap: 7px; }
.account-menu__card h2 { font-size: .75rem; color: var(--accent); }
.account-menu__card header span { overflow-wrap: anywhere; font-size: 1.1rem; font-weight: 750; }
.account-menu__card dl { display: grid; gap: 14px; margin: 20px 0; }
.account-menu__card dt { margin-bottom: 5px; color: var(--faint); font-size: .72rem; }
.account-menu__card dd { margin: 0; overflow-wrap: anywhere; font-size: .85rem; }
.account-menu__links { display: grid; gap: 8px; padding-top: 14px; border-top: 1px solid var(--line); }
.account-menu__links a, .account-menu__links button { display: flex; min-height: 44px; align-items: center; justify-content: space-between; width: 100%; padding: 10px 12px; border: 1px solid transparent; border-radius: 8px; background: var(--surface-2); color: var(--text); font-size: .82rem; font-weight: 650; cursor: pointer; }
.account-menu__links a:hover, .account-menu__links button:hover { border-color: var(--line-strong); background: var(--surface-3); }
.account-menu__links .account-menu__logout { margin-top: 4px; background: transparent; color: var(--negative); }
.account-menu__links .account-menu__admin { display: none; }
@media (max-width: 1180px) and (min-width: 901px) { .account-menu__identity { display: none; } }
@media (max-width: 900px) { .account-menu__links .account-menu__admin { display: flex; } }
@media (max-width: 620px) {
  .account-menu__trigger { max-width: 165px; gap: 7px; padding: 5px 8px; }
  .account-menu__identity strong { max-width: 80px; }
}
@media (max-width: 360px) { .account-menu__avatar { display: none; } }
</style>
